import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Lock, Camera, Fingerprint, Trash2, Plus, Smartphone, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { registerPasskey, isPasskeySupported } from '@/lib/passkey';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, token, refreshUser, hasPerm } = useAuth();
  const isAdmin = hasPerm('create_users') || hasPerm('edit_users') || hasPerm('delete_users');
  const fileRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', mobile: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Passkey state
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const passkeysSupported = isPasskeySupported();

  useEffect(() => {
    api.get('/api/auth/me').then(r => {
      setProfile(r.data);
      setForm({ name: r.data.name || '', email: r.data.email || '', mobile: r.data.mobile || '' });
    }).catch(() => toast.error('Failed to load profile'));
  }, []);

  const handleSave = async () => {
    const payload = { mobile: form.mobile };
    if (isAdmin) {
      if (!form.name || !form.email) { toast.error(t('fill_required')); return; }
      payload.name = form.name;
      payload.email = form.email;
    }
    setSaving(true);
    try {
      const res = await api.put('/api/auth/profile', payload);
      setProfile(res.data);
      toast.success(t('profile_updated'));
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error(t('fill_required')); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error(t('passwords_mismatch')); return; }
    if (pwForm.new_password.length < 6) { toast.error(t('password_too_short')); return; }
    setChangingPw(true);
    try {
      await api.put('/api/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success(t('password_changed'));
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setChangingPw(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/auth/upload-avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(p => ({ ...p, avatar_url: res.data.avatar_url }));
      toast.success(t('avatar_updated') || 'Avatar updated');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const roleLabel = { admin: 'Admin', account_manager: 'Account Manager', sponsor: 'Sponsor' };

  // Load passkeys
  const loadPasskeys = () => {
    api.get('/api/passkeys').then(r => setPasskeys(r.data)).catch(() => {});
  };
  useEffect(() => { if (passkeysSupported) loadPasskeys(); }, []);

  const handleRegisterPasskey = async () => {
    const name = passkeyName.trim() || 'My Passkey';
    setRegisteringPasskey(true);
    try {
      const token = localStorage.getItem('isp_token');
      await registerPasskey(token, name);
      toast.success(`Passkey "${name}" registered successfully`);
      setPasskeyName('');
      loadPasskeys();
    } catch (err) {
      if (err.name === 'NotAllowedError') return; // User cancelled
      toast.error(err.message || 'Failed to register passkey');
    } finally { setRegisteringPasskey(false); }
  };

  const handleDeletePasskey = async (pk) => {
    if (!window.confirm(`Remove passkey "${pk.name}"? You can re-register it later.`)) return;
    try {
      await api.delete(`/api/passkeys/${pk.id}`);
      toast.success(`Passkey "${pk.name}" removed`);
      loadPasskeys();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="profile-page" className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('profile')}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{t('manage_profile')}</p>
      </div>

      <Card className="bg-card border-border shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <div className="relative group">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-border" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xl font-semibold">
                  {profile?.name?.[0] || '?'}
                </div>
              )}
              <button
                data-testid="avatar-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera size={18} className="text-white" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{profile?.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge className="bg-muted text-muted-foreground text-xs font-normal pointer-events-none select-none">{roleLabel[profile?.role] || profile?.role}</Badge>
                <Badge className={`text-xs font-normal pointer-events-none select-none ${profile?.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{profile?.status}</Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <Separator className="bg-border" />
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">{t('name')}</Label>
              {isAdmin ? (
                <Input data-testid="profile-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-background border-border h-10 text-sm mt-1" />
              ) : (
                <Input data-testid="profile-name" value={form.name} readOnly disabled className="bg-muted/50 border-border h-10 text-sm mt-1 cursor-not-allowed opacity-70" />
              )}
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t('email')}</Label>
              {isAdmin ? (
                <Input data-testid="profile-email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="bg-background border-border h-10 text-sm mt-1" />
              ) : (
                <Input data-testid="profile-email" type="email" value={form.email} readOnly disabled className="bg-muted/50 border-border h-10 text-sm mt-1 cursor-not-allowed opacity-70" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">{t('mobile')}</Label>
              <Input data-testid="profile-mobile" value={form.mobile} onChange={e => setForm(p => ({...p, mobile: e.target.value}))} placeholder="Optional" className="bg-background border-border h-10 text-sm mt-1" />
            </div>
          </div>
          <Button data-testid="save-profile-btn" onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm h-9">
            {saving ? '...' : t('save_changes')}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Lock size={15} strokeWidth={1.8} />
            {t('change_password')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm text-muted-foreground">{t('current_password')}</Label>
            <Input data-testid="current-password" type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({...p, current_password: e.target.value}))} className="bg-background border-border h-10 text-sm mt-1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">{t('new_password')}</Label>
              <Input data-testid="new-password" type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({...p, new_password: e.target.value}))} className="bg-background border-border h-10 text-sm mt-1" />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t('confirm_password')}</Label>
              <Input data-testid="confirm-password" type="password" value={pwForm.confirm_password} onChange={e => setPwForm(p => ({...p, confirm_password: e.target.value}))} className="bg-background border-border h-10 text-sm mt-1" />
            </div>
          </div>
          <Button data-testid="change-pw-btn" onClick={handleChangePassword} disabled={changingPw} variant="outline" className="text-sm h-9 border-border">
            {changingPw ? '...' : t('change_password')}
          </Button>
        </CardContent>
      </Card>

      {/* Passkeys */}
      {passkeysSupported && (
        <Card className="bg-card border-border shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Fingerprint size={15} strokeWidth={1.8} />
              Passkeys
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Sign in with Face ID, Touch ID, fingerprint, or a security key — no password needed.
            </p>

            {/* Registered passkeys list */}
            {passkeys.length > 0 && (
              <div className="space-y-2">
                {passkeys.map(pk => (
                  <div key={pk.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-border bg-muted/30" data-testid={`passkey-item-${pk.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        {pk.device_type === 'multi_device' ? <Smartphone size={14} className="text-muted-foreground" /> : <Monitor size={14} className="text-muted-foreground" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{pk.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          Added {new Date(pk.created_at).toLocaleDateString()}
                          {pk.last_used_at && ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}`}
                          {pk.backed_up && ' · Synced'}
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid={`delete-passkey-${pk.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePasskey(pk)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Register new passkey */}
            <div className="flex items-center gap-2">
              <Input
                data-testid="passkey-name-input"
                value={passkeyName}
                onChange={e => setPasskeyName(e.target.value)}
                placeholder="e.g. iPhone Face ID, MacBook Touch ID"
                className="bg-background border-border h-9 text-sm flex-1"
              />
              <Button
                data-testid="register-passkey-btn"
                onClick={handleRegisterPasskey}
                disabled={registeringPasskey}
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs h-9 px-4 whitespace-nowrap"
              >
                <Plus size={14} className="mr-1" />
                {registeringPasskey ? 'Registering...' : 'Add Passkey'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
